"use client";

import * as React from "react";
import Box from "@mui/material/Box";
import { keyframes } from "@emotion/react";
import useDevice from "@/hooks/useDevice";

const questions = [
  "🧴 What's trending in beauty?",
  "🚚 What's booming in electronics?",
  "💬 Top products in Tier 2 cities?",
  "🛒 What's hot in men's fashion in last 2 weeks?",
  "📉 Low RTO category in Tier 2?",
  "📦 What's trending in home & kitchen?",
  "🛍️ Trending items in kids category?",
  "🧥 Clothing trends in Maharashtra",
  "🧠 protein supplements in Maharashtra",
  "🛒 Which city has the highest order value?",
  "🏆 top emerging cities in ayurveda?",
  "🔍 Top-selling items in Delhi?",
  "📍 RTO in fashion vs beauty"
];

const questions2 = [
  "🧴 Most ordered skincare products?",
  "🚚 Fastest growing product this week",
  "💬 Tier 1 cities with low RTO?",
  "🛒 High Rto cities in Rajasthan",
  "📉 Low RTO category in Tier 2?",
  "📦 trending categories in Tier 3 cities?",
  "🛍️ Trending items in kids category?",
  "🧥 Which city has the highest order value?",
  "🧠 Which category saw the highest growth",
  "🛒 High AOV category in prepaid mode?",
];
// Removes the leading emoji and space from a question string
function stripEmoji(question: string): string {
  return question.replace(/^\p{Emoji_Presentation}\s*/u, '').replace(/^[^\p{L}\p{N}]+/u, '').trim();
}

// Define a marquee animation that moves its container left by exactly one "pass" width
const marquee = keyframes`
  0% { transform: translateX(0); }
 100% { transform: translateX(calc(-1 * var(--marq-w))); }
`;

export default function QuestionMarquee({onClick} :any) {
  const groupRef = React.useRef<HTMLDivElement>(null);
  const [passWidth, setPassWidth] = React.useState(0);
  const isMobile = useDevice()
  // Measure the width of one full set of questions
  React.useEffect(() => {
    function updateWidth() {
      if (groupRef.current) {
        setPassWidth(groupRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener("resize", updateWidth);
    return () => window.removeEventListener("resize", updateWidth);
  }, []);

  const duration = `${passWidth / 20}s`;
  // you can tweak divisor for speed: higher divisor → slower

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        overflowX: "hidden",
        py: 2,
        mt: isMobile ? 0 : 10,
        // expose the measured width to CSS
        fontFamily: '"DM Mono", monospace',
        "--marq-w": `${passWidth}px`,
      }}
    >
      {/* Top row: scroll left */}
      <Box
        sx={{
          display: "flex",
          width: "calc(var(--marq-w) * 2)",
          whiteSpace: "nowrap",
          animation: `${marquee} ${duration} linear infinite`,
        }}
      >
        {/* First pass */}
        <Box
          ref={groupRef}
          sx={{ display: "flex", whiteSpace: "nowrap", gap: 3 }}
        >
          {questions.map((q, i) => (
            <Box
              key={`t1-${i}`}
              component="span"
              sx={{
                px: 4,
                fontSize: "0.9rem",
                color: "text.primary",
                background: "#A8C0FF1A",
                p: "10px 12px",
                borderRadius: 10,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "#A8C0FF80",
                  cursor: "pointer",
                },
              }}
              onClick={()=>onClick(stripEmoji(q))}
            >
              {q}
            </Box>
          ))}
        </Box>
        {/* Duplicate pass */}
        <Box sx={{ display: "flex", whiteSpace: "nowrap", gap: 3 }}>
          {questions.map((q, i) => (
            <Box
              key={`t2-${i}`}
              component="span"
              sx={{
                px: 4,
                fontSize: "0.9rem",
                color: "text.primary",
                background: "#A8C0FF1A",
                p: "10px 12px",
                borderRadius: 10,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "#A8C0FF80",
                  cursor: "pointer",
                },
              }}
              onClick={()=>onClick(stripEmoji(q))}
            >
              {q}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Bottom row: scroll right (reverse direction) */}
      <Box
        sx={{
          display: "flex",
          width: "calc(var(--marq-w) * 2)",
          whiteSpace: "nowrap",
          animation: `${marquee} ${duration} linear infinite`,
          animationDirection: "reverse",
          mt: 3,
        }}
      >
        {/* First pass */}
        <Box sx={{ display: "flex", whiteSpace: "nowrap", gap: 3 }}>
          {questions2.map((q, i) => (
            <Box
              key={`b1-${i}`}
              component="span"
              sx={{
                px: 4,
                fontSize: "0.9rem",
                color: "text.primary",
                background: "#A8C0FF1A",
                p: "10px 12px",
                borderRadius: 10,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "#A8C0FF80",
                  cursor: "pointer",
                },
              }}
              onClick={()=>onClick(stripEmoji(q))}
            >
              {q}
            </Box>
          ))}
        </Box>
        {/* Duplicate pass */}
        <Box sx={{ display: "flex", whiteSpace: "nowrap", gap: 3 }}>
          {questions2.map((q, i) => (
            <Box
              key={`b2-${i}`}
              component="span"
              sx={{
                px: 4,
                fontSize: "0.9rem",
                color: "text.primary",
                background: "#A8C0FF1A",
                p: "10px 12px",
                borderRadius: 10,
                transition: "background 0.2s, color 0.2s",
                "&:hover": {
                  background: "#A8C0FF80",
                  cursor: "pointer",
                },
              }}
              onClick={()=>onClick(stripEmoji(q))}
            >
              {q}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Fade‐out masks */}
      {!isMobile  && <Box
        sx={{
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          bottom: 0,
          left: 0,
          width: "10vw",
          background: "linear-gradient(to right, #000, transparent)",
          zIndex: 2,
        }}
      />}
      {!isMobile && <Box
        sx={{
          pointerEvents: "none",
          position: "absolute",
          top: 0,
          bottom: 0,
          right: 0,
          width: "10vw",
          background: "linear-gradient(to left, #000, transparent)",
          zIndex: 2,
        }}
      />}
    </Box>
  );
}
